// Basketball game scoresheets — blank, printable, ONE team / ONE game, ONE PAGE.
// The admin downloads a generic blank sheet (team, players, and numbers left
// blank) and prints copies for scorekeepers to fill in during games.
//
// Columns are driven by the division's SELECTED stats (config.gameStats.basketball):
// every layout always has a jersey "#" column, a Player Name column, a Personal
// Fouls (1-5) marking area, and a PTS column; the other selected stats become
// columns (via basketballStatMetadata headerNames), in the canonical stat order.
//
// Every variant is sized to fit on a SINGLE landscape-letter page: the player-row
// height adapts to the row count + whatever extra sections the variant draws, so
// nothing ever spills to a second page.
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PAGE_MARGIN, saveDoc, scoresheetFilename } from './pdfCommon';
import {
  basketballStatMetadata,
  STAT_FIELDS_BY_SPORT,
  DEFAULT_GAME_STATS_BY_SPORT,
} from '@staty/shared/schemas/stat';

export interface BasketballScoresheetVariant {
  id: string;
  label: string;
}

// The download-menu options (mirrors SCORESHEET_VARIANTS for the schedule sheets).
export const BASKETBALL_SCORESHEET_VARIANTS: BasketballScoresheetVariant[] = [
  { id: 'box-score', label: 'Box Score (stat grid)' },
  { id: 'full', label: 'Full Scoresheet (stats + periods)' },
  { id: 'running', label: 'Running Score (traditional)' },
  { id: 'tournament', label: 'Tournament (running score + halves)' },
];

export interface BasketballScoresheetData {
  division?: any;
  sport?: string;
  statKeys?: string[]; // resolved selected keys (config.gameStats[sport])
  maxPlayers?: number; // player row count
  leagueName?: string;
}

const VARIANT_LABELS: Record<string, string> = {
  'box-score': 'Box Score',
  full: 'Full Scoresheet',
  running: 'Running Score',
  tournament: 'Tournament',
};

// Landscape US-letter in points.
const PAGE_W = 792;
const PAGE_H = 612;
const TABLE_HEAD_H = 22;

function newDoc(): jsPDF {
  return new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
}

function lastY(doc: jsPDF): number {
  return (doc as any).lastAutoTable?.finalY ?? PAGE_MARGIN;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

// Per-player row height that makes the whole sheet fit one page: the space left
// after the header + the variant's extra sections, divided across the rows
// (+ TOTALS), then trimmed ~10% (shorter rows) and clamped so rows stay legible
// but never overflow.
function fitRowHeight(rows: number, startY: number, extrasHeight: number): number {
  const avail = PAGE_H - PAGE_MARGIN - startY - extrasHeight - 8;
  return clamp(((avail - TABLE_HEAD_H) / (rows + 1)) * 0.9, 10, 27);
}

// Analytic y after the header block (mirrors drawGameHeader) so callers can size
// the table and decide whether to keep the title before drawing.
function headerBottomY(hasSubtitle: boolean, showTitle: boolean): number {
  let y = PAGE_MARGIN + 4;
  if (showTitle) y += 15;
  if (hasSubtitle) y += 13;
  y += 14 + 12;
  return y;
}

// Ordered stat keys for the columns: PTS always first, then the other selected
// stats in canonical STAT_FIELDS order. `fouls` is excluded here — it's rendered
// as the Personal Fouls (1-5) area instead. Falls back to the sport defaults when
// nothing is selected.
function resolveStatKeys(statKeys: string[] | undefined, sport: string): string[] {
  const valid = STAT_FIELDS_BY_SPORT[sport] || STAT_FIELDS_BY_SPORT.basketball;
  let keys = (statKeys || []).filter((k) => valid.includes(k));
  if (!keys.length) keys = DEFAULT_GAME_STATS_BY_SPORT[sport] || DEFAULT_GAME_STATS_BY_SPORT.basketball;
  const set = new Set(keys.filter((k) => k !== 'fouls'));
  set.add('points'); // a scoresheet always needs a score
  return ['points', ...valid.filter((k) => k !== 'points' && k !== 'fouls' && set.has(k))];
}

function headerName(key: string): string {
  return basketballStatMetadata[key]?.headerName || key;
}

// Blank header: title (optional — dropped when the sheet is tight on space) +
// team/opponent/date/time/location write-ins.
function drawGameHeader(doc: jsPDF, title: string, subtitle: string | undefined, showTitle: boolean): number {
  const x = PAGE_MARGIN;
  let y = PAGE_MARGIN + 4;
  if (showTitle) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text(title, x, y);
    y += 15;
  }
  if (subtitle) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(110);
    doc.text(subtitle, x, y);
    doc.setTextColor(0);
    y += 13;
  }
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text('Team: ______________________________        vs        Opponent: ______________________________', x, y);
  y += 14;
  doc.text('Date: ______________       Time: ______________       Location: ______________________________', x, y);
  y += 12;
  return y;
}

// The per-player stat table (used by every variant). Blank rows for the
// scorekeeper: # | Player Name | Personal Fouls (1-5) | PTS | <other stats>,
// plus a TOTALS row. `rowHeight` is pre-computed to fit the page.
function drawStatTable(doc: jsPDF, statKeys: string[], rows: number, startY: number, rowHeight: number): number {
  const fontSize = rowHeight >= 20 ? 9 : rowHeight >= 15 ? 8 : 7;
  const pad = rowHeight >= 20 ? 4 : rowHeight >= 15 ? 3 : 2;
  const statHeads = statKeys.map(headerName);
  const head: any = [['#', 'Player Name', 'Personal Fouls', ...statHeads]];
  const body: any[] = [];
  for (let i = 0; i < rows; i += 1) {
    body.push(['', '', '1   2   3   4   5', ...statKeys.map(() => '')]);
  }
  body.push(['', 'TOTALS', '', ...statKeys.map(() => '')]);

  const columnStyles: any = {
    0: { cellWidth: 26, halign: 'center' },
    1: { cellWidth: 150, halign: 'left' },
    2: { cellWidth: 96, halign: 'center' },
  };
  // Stat columns explicitly sized so PTS (always the first stat column, index 3)
  // is 50% wider than the rest, and the row exactly fills the usable width.
  const n = statKeys.length;
  const remaining = PAGE_W - 2 * PAGE_MARGIN - (26 + 150 + 96);
  const otherW = remaining / (n + 0.5);
  columnStyles[3] = { cellWidth: otherW * 1.5 };
  for (let i = 1; i < n; i += 1) columnStyles[3 + i] = { cellWidth: otherW };

  autoTable(doc, {
    startY,
    head,
    body,
    theme: 'grid',
    styles: { fontSize, cellPadding: pad, valign: 'middle', halign: 'center', minCellHeight: rowHeight, lineColor: [120, 120, 120] },
    headStyles: { fillColor: [235, 235, 235], textColor: 20, fontStyle: 'bold', halign: 'center', minCellHeight: TABLE_HEAD_H },
    bodyStyles: { fillColor: [255, 255, 255] },
    columnStyles,
    didParseCell: (d: any) => {
      if (d.section === 'body' && d.row.index === body.length - 1 && d.column.index === 1) {
        d.cell.styles.fontStyle = 'bold';
      }
    },
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
  });
  return lastY(doc);
}

// Score-by-period grid (This Team / Opponent). Common 4-period + OT + Final.
function drawPeriodsBlock(doc: jsPDF, startY: number): number {
  autoTable(doc, {
    startY: startY + 10,
    head: [['Score by Period', '1', '2', '3', '4', 'OT', 'FINAL']],
    body: [
      ['This Team', '', '', '', '', '', ''],
      ['Opponent', '', '', '', '', '', ''],
    ],
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 4, halign: 'center', valign: 'middle', minCellHeight: 18, lineColor: [120, 120, 120] },
    headStyles: { fillColor: [235, 235, 235], textColor: 20, fontStyle: 'bold' },
    columnStyles: { 0: { cellWidth: 96, halign: 'left', fontStyle: 'bold' }, 1: { cellWidth: 42 }, 2: { cellWidth: 42 }, 3: { cellWidth: 42 }, 4: { cellWidth: 42 }, 5: { cellWidth: 42 }, 6: { cellWidth: 56 } },
    tableWidth: 'wrap',
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
  });
  return lastY(doc);
}

// Team fouls (bonus / double-bonus markers) + timeout boxes.
function drawFoulsTimeouts(doc: jsPDF, startY: number): number {
  let y = startY + 18;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Team Fouls', PAGE_MARGIN, y);
  doc.setFont('helvetica', 'normal');
  doc.text('1    2    3    4    5    6    [ BONUS ]    7    8    9    10    [ DOUBLE BONUS ]', PAGE_MARGIN + 74, y);
  y += 16;
  doc.setFont('helvetica', 'bold');
  doc.text('Timeouts', PAGE_MARGIN, y);
  doc.setFont('helvetica', 'normal');
  let bx = PAGE_MARGIN + 74;
  for (let i = 0; i < 5; i += 1) {
    doc.rect(bx, y - 9, 11, 11);
    bx += 24;
  }
  return y + 4;
}

// Traditional team cumulative running-score grid (cross off each point scored).
function drawRunningGrid(doc: jsPDF, startY: number, max = 120, perRow = 30): number {
  const rowsUsed = Math.ceil(max / perRow);
  const usableW = PAGE_W - 2 * PAGE_MARGIN;
  const cw = usableW / perRow;
  const ch = 12;
  let y = startY + 16;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Team Running Score  (cross off each point scored)', PAGE_MARGIN, y - 4);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  for (let n = 1; n <= max; n += 1) {
    const idx = n - 1;
    const col = idx % perRow;
    const row = Math.floor(idx / perRow);
    const cx = PAGE_MARGIN + col * cw;
    const cy = y + row * ch;
    doc.rect(cx, cy, cw, ch);
    doc.text(String(n), cx + cw / 2, cy + ch / 2 + 2, { align: 'center' });
  }
  return y + rowsUsed * ch;
}

// ── variant builders (each fits one page) ────────────────────────────────────

// Pick a row height that fits, dropping the "Basketball Scoresheet" title when
// the sheet is tight so the rows get a little more room, then draw the header.
function headerAndRows(
  doc: jsPDF, title: string, subtitle: string | undefined, rows: number, extras: number,
): { y: number; rh: number } {
  const hasSub = !!subtitle;
  let showTitle = true;
  let rh = fitRowHeight(rows, headerBottomY(hasSub, showTitle) + 6, extras);
  if (rh < 14) {
    showTitle = false;
    rh = fitRowHeight(rows, headerBottomY(hasSub, showTitle) + 6, extras);
  }
  const y = drawGameHeader(doc, title, subtitle, showTitle) + 6;
  return { y, rh };
}

function buildBoxScore(doc: jsPDF, statKeys: string[], rows: number, title: string, subtitle?: string): void {
  const { y, rh } = headerAndRows(doc, title, subtitle, rows, 0);
  drawStatTable(doc, statKeys, rows, y, rh);
}

function buildFull(doc: jsPDF, statKeys: string[], rows: number, title: string, subtitle?: string): void {
  const extras = 110; // periods + team fouls + timeouts
  const { y, rh } = headerAndRows(doc, title, subtitle, rows, extras);
  let yy = drawStatTable(doc, statKeys, rows, y, rh);
  yy = drawPeriodsBlock(doc, yy);
  drawFoulsTimeouts(doc, yy);
}

function buildRunning(doc: jsPDF, statKeys: string[], rows: number, title: string, subtitle?: string): void {
  const extras = 190; // running grid + periods + team fouls + timeouts
  const { y, rh } = headerAndRows(doc, title, subtitle, rows, extras);
  let yy = drawStatTable(doc, statKeys, rows, y, rh);
  yy = drawRunningGrid(doc, yy);
  yy = drawPeriodsBlock(doc, yy);
  drawFoulsTimeouts(doc, yy);
}

// ── 'tournament' variant (owner template 2026-10-07) ────────────────────────
// Replicates the classic per-team tournament sheet the owner supplied
// (_Scoresheet TEMPLATE.pdf — "Tigers Invitational" style): meta boxes top
// right, team/division/game-number row, a 1–110 running-score strip, a
// 13-row player table with Personal Fouls 1-5 and First/Second Half +
// Overtime + Total scoring columns, then Timeouts / per-half Team Fouls
// (shaded BONUS at 7 and DOUBLE at 10) / Final + Opponent score boxes and
// officials' signature lines. Stats columns deliberately DON'T apply here —
// this is the hand-tally sheet, not a stat grid.

const DARK: [number, number, number] = [30, 30, 30];
const GRID: [number, number, number] = [90, 90, 90];

function drawTournamentSheet(doc: jsPDF, title: string): void {
  const x0 = PAGE_MARGIN;
  const usableW = PAGE_W - 2 * PAGE_MARGIN;
  let y = PAGE_MARGIN;

  // Title (left) + Date/Time/Gym Location boxes (right).
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text(title, x0, y + 10, { maxWidth: usableW * 0.5 });
  const metaW = 300;
  const metaX = x0 + usableW - metaW;
  const metaCols = [60, 60, 180];
  const metaLabels = ['Date', 'Time', 'Gym Location'];
  doc.setFontSize(8.5);
  let cx = metaX;
  for (let i = 0; i < 3; i += 1) {
    doc.setDrawColor(...GRID);
    doc.setFillColor(235, 235, 235);
    doc.rect(cx, y, metaCols[i], 14, 'FD');
    doc.rect(cx, y + 14, metaCols[i], 18);
    doc.text(metaLabels[i], cx + metaCols[i] / 2, y + 9.5, { align: 'center' });
    cx += metaCols[i];
  }
  y += 40;

  // Team Name / Color | Division | Game Number (1-14 circles).
  const teamW = usableW * 0.3;
  const divW = usableW * 0.3;
  const gameW = usableW - teamW - divW;
  doc.setFillColor(235, 235, 235);
  doc.rect(x0, y, teamW, 12, 'FD');
  doc.rect(x0 + teamW, y, divW, 12, 'FD');
  doc.setFillColor(...DARK);
  doc.rect(x0 + teamW + divW, y, gameW, 12, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(0);
  doc.text('Team Name / Color', x0 + 3, y + 8.5);
  doc.text('Division', x0 + teamW + 3, y + 8.5);
  doc.setTextColor(255);
  doc.text('Game Number', x0 + teamW + divW + gameW / 2, y + 8.5, { align: 'center' });
  doc.setTextColor(0);
  doc.rect(x0, y + 12, teamW, 22);
  doc.rect(x0 + teamW, y + 12, divW, 22);
  doc.rect(x0 + teamW + divW, y + 12, gameW, 22);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  const circles = 14;
  const cgap = gameW / circles;
  for (let i = 1; i <= circles; i += 1) {
    const ccx = x0 + teamW + divW + cgap * (i - 0.5);
    doc.circle(ccx, y + 23, 5.2);
    doc.text(String(i), ccx, y + 25.3, { align: 'center' });
  }
  y += 40;

  // RUNNING SCORE strip: label cell + 2 rows × 55.
  const labelW = 52;
  const cells = 55;
  const cellW = (usableW - labelW) / cells;
  const stripH = 11;
  doc.setFillColor(235, 235, 235);
  doc.rect(x0, y, labelW, stripH * 2, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.text('RUNNING', x0 + labelW / 2, y + 8, { align: 'center' });
  doc.text('SCORE', x0 + labelW / 2, y + 17.5, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  for (let r = 0; r < 2; r += 1) {
    for (let c = 0; c < cells; c += 1) {
      const n = r * cells + c + 1;
      const rx = x0 + labelW + c * cellW;
      const ry = y + r * stripH;
      doc.rect(rx, ry, cellW, stripH);
      doc.text(String(n), rx + cellW / 2, ry + stripH / 2 + 2, { align: 'center' });
    }
  }
  y += stripH * 2 + 6;

  // Player table: No. | Players Name | Personal Fouls 1-5 | halves | OT | Total.
  const PLAYER_ROWS = 13;
  const foulW = 100;
  const head: any = [[
    { content: 'No.', styles: { halign: 'center' } },
    { content: 'Players Name', styles: { halign: 'center' } },
    { content: 'Personal Fouls', colSpan: 5, styles: { halign: 'center' } },
    { content: 'First Half', styles: { halign: 'center' } },
    { content: 'Second Half', styles: { halign: 'center' } },
    { content: 'Overtime', styles: { halign: 'center' } },
    { content: 'Total', styles: { halign: 'center' } },
  ]];
  const body: any[] = [];
  for (let i = 0; i < PLAYER_ROWS; i += 1) body.push(['', '', '1', '2', '3', '4', '5', '', '', '', '']);
  const firstHalfW = 150;
  const secondHalfW = 150;
  const otW = 62;
  const totalW = 52;
  const noW = 30;
  const nameW = usableW - noW - foulW - firstHalfW - secondHalfW - otW - totalW;
  autoTable(doc, {
    startY: y,
    head,
    body,
    theme: 'grid',
    styles: { fontSize: 7, cellPadding: 1.5, valign: 'middle', halign: 'center', minCellHeight: 17.5, lineColor: GRID },
    headStyles: { fillColor: [235, 235, 235], textColor: 20, fontStyle: 'bold', minCellHeight: 14 },
    bodyStyles: { fillColor: [255, 255, 255], textColor: 150 },
    columnStyles: {
      0: { cellWidth: noW },
      1: { cellWidth: nameW, halign: 'left' },
      2: { cellWidth: foulW / 5 }, 3: { cellWidth: foulW / 5 }, 4: { cellWidth: foulW / 5 }, 5: { cellWidth: foulW / 5 }, 6: { cellWidth: foulW / 5 },
      7: { cellWidth: firstHalfW },
      8: { cellWidth: secondHalfW },
      9: { cellWidth: otW },
      10: { cellWidth: totalW },
    },
    margin: { left: x0, right: PAGE_MARGIN },
  });
  y = lastY(doc) + 8;

  // Timeouts | 1st Half Team Fouls | 2nd Half Team Fouls | Final Score.
  const foulLabels = ['1', '2', '3', '4', '5', '6', 'Bonus', '8', '9', 'Double'];
  const toLabels = ['Full', 'Full', 'Full', ':20', ':20'];
  const finalW = 70;
  const sectionW = usableW - finalW;
  const toW = sectionW * 0.18;
  const halfW = (sectionW - toW) / 2;
  const headH = 12;
  const rowH = 16;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  const section = (sx: number, w: number, label: string, labels: string[]) => {
    doc.setFillColor(235, 235, 235);
    doc.setTextColor(0);
    doc.rect(sx, y, w, headH, 'FD');
    doc.text(label, sx + w / 2, y + 8.5, { align: 'center' });
    const cw = w / labels.length;
    labels.forEach((l, i) => {
      const dark = l === 'Bonus' || l === 'Double';
      if (dark) {
        doc.setFillColor(...DARK);
        doc.rect(sx + i * cw, y + headH, cw, rowH, 'FD');
        doc.setTextColor(255);
      } else {
        doc.rect(sx + i * cw, y + headH, cw, rowH);
        doc.setTextColor(0);
      }
      doc.text(l, sx + i * cw + cw / 2, y + headH + 7, { align: 'center' });
      doc.setTextColor(0);
    });
  };
  section(x0, toW, 'Timeouts', toLabels);
  section(x0 + toW, halfW, '1st Half Team Fouls', foulLabels);
  section(x0 + toW + halfW, halfW, '2nd Half Team Fouls', foulLabels);
  // Final Score box (dark label, write-in below).
  doc.setFillColor(...DARK);
  doc.rect(x0 + sectionW, y, finalW, headH, 'FD');
  doc.setTextColor(255);
  doc.text('Final Score', x0 + sectionW + finalW / 2, y + 8.5, { align: 'center' });
  doc.setTextColor(0);
  doc.rect(x0 + sectionW, y + headH, finalW, rowH);
  y += headH + rowH + 14;

  // Opponent's Score box (right) + officials' lines (left).
  doc.setFillColor(...DARK);
  doc.rect(x0 + sectionW, y - 4, finalW, headH, 'FD');
  doc.setTextColor(255);
  doc.text("Opponent's Score", x0 + sectionW + finalW / 2, y + 4.5, { align: 'center' });
  doc.setTextColor(0);
  doc.rect(x0 + sectionW, y - 4 + headH, finalW, rowH);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  const lineY1 = y + 12;
  const lineY2 = y + 34;
  const line = (lx: number, w: number, label: string, ly: number) => {
    doc.line(lx, ly, lx + w, ly);
    doc.text(label, lx, ly + 9);
  };
  line(x0, 150, 'Referee', lineY1);
  line(x0 + 180, 220, 'Referee', lineY1);
  line(x0 + 430, 180, 'Opponent', lineY1);
  line(x0, 150, 'Scorekeeper', lineY2);
  line(x0 + 180, 220, 'Timer', lineY2);
}

function buildTournament(doc: jsPDF, title: string, subtitle?: string): void {
  // The template puts the tournament/league name as the single title line;
  // division rides along when present.
  const heading = [subtitle, undefined].filter(Boolean).length ? String(subtitle) : title;
  drawTournamentSheet(doc, heading || title);
}

// Build the basketball scoresheet jsPDF doc for the given variant (no download).
// Callers can preview it (`doc.output('bloburl')`) and/or save it (`saveDoc`).
export function buildBasketballScoresheetDoc(variantId: string, data: BasketballScoresheetData): jsPDF {
  const sport = data.sport || 'basketball';
  const statKeys = resolveStatKeys(data.statKeys, sport);
  const rows = Math.max(1, Math.min(20, Number(data.maxPlayers) || 11)); // 11 = print-layout row count, not a platform default (ADR 0035)
  const title = 'Basketball Scoresheet';
  const subtitle = [data.leagueName, data?.division?.name].filter(Boolean).join(', ') || undefined;

  const doc = newDoc();
  switch (variantId) {
    case 'full':
      buildFull(doc, statKeys, rows, title, subtitle);
      break;
    case 'running':
      buildRunning(doc, statKeys, rows, title, subtitle);
      break;
    case 'tournament':
      buildTournament(doc, title, subtitle);
      break;
    case 'box-score':
    default:
      buildBoxScore(doc, statKeys, rows, title, subtitle);
      break;
  }
  return doc;
}

// Canonical download filename for a variant (kept identical between the direct
// download and the preview-modal Download button).
export function basketballScoresheetFilename(variantId: string, data: BasketballScoresheetData): string {
  const label = VARIANT_LABELS[variantId] || 'Scoresheet';
  return scoresheetFilename(data?.division?.name, `Basketball ${label}`);
}

// Build + download the basketball scoresheet for the given variant.
export function generateBasketballScoresheet(variantId: string, data: BasketballScoresheetData): void {
  const doc = buildBasketballScoresheetDoc(variantId, data);
  saveDoc(doc, basketballScoresheetFilename(variantId, data));
}
